import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, requestBlob } from "@/lib/api";
import {
  REPORT_PERIOD_PRESETS,
  endpoints,
  type DashboardSummaryResponse,
  type Gstr1Section,
  type OutstandingAgeingResponse,
  type ReportCustomersResponse,
  type ReportPeriodPreset,
  type ReportSummaryResponse,
} from "@/lib/endpoints";
import { downloadBlob } from "@/lib/pdf";
import { queryKeys } from "@/lib/query-keys";

/**
 * The Reports screen's data, from the client's side (§11.2, §17.4).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * NO ARITHMETIC LIVES IN THIS FILE, AND NONE MAY. Not a subtraction, not a
 * `reduce`, not a "quick" net for a caption. §6 makes the server the only
 * authority on totals, and this screen's whole reason for existing is that its
 * figures go to a Chartered Accountant and become a filed return. Every number
 * the screen renders — `net`, the customer rows' `netBilledPaise`, the ageing
 * buckets, the totals under the table — arrives already computed.
 *
 * THE PERIOD IS RESOLVED SERVER-SIDE, TOO, AND THAT IS THE POINT OF SENDING A
 * PRESET RATHER THAN TWO DATES. `?preset=thisMonth` is spelled identically here
 * and in `packages/types/src/reports.ts`, so the chip the owner pressed reaches
 * the API unchanged; `resolveReportWindow` turns it into a window ONCE, in
 * Asia/Kolkata, and every figure and every CSV in this screen describes that one
 * window. A client that computed its own `from`/`to` would put a second copy of
 * April-boundary arithmetic in the app — and the failure that produces is a CSV
 * for a month the owner was not looking at.
 *
 * THE LINKS USE THE DATES THE SERVER SENT BACK (`period.from` / `period.to`),
 * never dates this file derived, for exactly the same reason.
 * ═════════════════════════════════════════════════════════════════════════════
 */

/* -------------------------------------------------------------------------- */
/* The period selector                                                         */
/* -------------------------------------------------------------------------- */

/**
 * §11.2 names THREE presets — "this month / last month / this FY" — and there
 * are three. A fourth is a spec change, not a convenience.
 *
 * The label keys are `reports.*` rather than the register's `list.*` because the
 * two say different things: the filter sheet's "This month" narrows a list, and
 * this one names the period every figure on the screen is about.
 */
export const REPORT_PRESET_LABEL_KEY: Record<ReportPeriodPreset, string> = {
  thisMonth: "reports.thisMonth",
  lastMonth: "reports.lastMonth",
  thisFy: "reports.thisYear",
};

/** The default: what he billed this month, which is why he opened the screen. */
export const DEFAULT_REPORT_PRESET: ReportPeriodPreset = "thisMonth";

function isReportPreset(value: string | null): value is ReportPeriodPreset {
  return value !== null && (REPORT_PERIOD_PRESETS as readonly string[]).includes(value);
}

/**
 * The chosen period, kept in the URL.
 *
 * IN THE URL RATHER THAN IN `useState`, so that `/reports?period=lastMonth`
 * survives a refresh, a back button and a link sent from the office desktop to
 * the phone — the same treatment every register in this app gives its filters.
 * On 3 September the owner opens Reports, taps "Last month", and that is the
 * page he can send himself; the export below inherits it, so the file and the
 * figures cannot describe different months.
 *
 * An unrecognised `?period=` falls back to the default rather than 400-ing the
 * screen: a mangled link should still show him something true.
 */
export function useReportPeriod(): {
  preset: ReportPeriodPreset;
  setPreset: (next: ReportPeriodPreset) => void;
} {
  const [params, setParams] = useSearchParams();
  const raw = params.get("period");
  const preset = isReportPreset(raw) ? raw : DEFAULT_REPORT_PRESET;

  const setPreset = useCallback(
    (next: ReportPeriodPreset) => {
      const updated = new URLSearchParams(params);
      updated.set("period", next);
      // `replace`: flipping between three chips is not three history entries the
      // back button should have to walk out of.
      setParams(updated, { replace: true });
    },
    [params, setParams],
  );

  return { preset, setPreset };
}

/** `?preset=thisMonth` — the ONE query string every reports request carries. */
export function reportPeriodQuery(preset: ReportPeriodPreset): string {
  return `?preset=${encodeURIComponent(preset)}`;
}

/* -------------------------------------------------------------------------- */
/* The figures                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * `GET /reports/summary` — total billed, tax charged and the counts, NET OF
 * CREDIT NOTES (§17.3), for the chosen period.
 *
 * `received` may be ABSENT from the response, and absent is not zero: the
 * `payments` module is off, or this caller lacks `payment.view`. The screen
 * renders no receipts caption at all in that case, because "Received: ₹0" is a
 * positive claim that nothing came in, made by a system that is not recording
 * receipts.
 */
export function useReportSummary(
  preset: ReportPeriodPreset,
): UseQueryResult<ReportSummaryResponse> {
  return useQuery({
    queryKey: queryKeys.reports.summary(preset),
    queryFn: () =>
      api.get<ReportSummaryResponse>(`${endpoints.reportsSummary}${reportPeriodQuery(preset)}`),
    /*
     * Half a minute, as the dashboard uses. These figures move when anybody
     * saves, cancels or credits a document — including his son on the shop
     * computer — and every such mutation already invalidates its own prefix.
     */
    staleTime: 30_000,
  });
}

/** `GET /reports/customers` — the customer-wise summary, largest net first. */
export function useReportCustomers(
  preset: ReportPeriodPreset,
): UseQueryResult<ReportCustomersResponse> {
  return useQuery({
    queryKey: queryKeys.reports.customers(preset),
    queryFn: () =>
      api.get<ReportCustomersResponse>(`${endpoints.reportsCustomers}${reportPeriodQuery(preset)}`),
    staleTime: 30_000,
  });
}

/**
 * `GET /reports/outstanding-ageing` — "who do I phone this morning" (§17.5).
 *
 * `enabled` IS THE CAPABILITY GATE, not a convenience: without `payment.view`
 * the request is never made, because the server would refuse it and asking would
 * only write a denial into the audit log (§2.1 audits denied attempts too).
 *
 * NO PERIOD. The endpoint takes none — outstanding is a to-do list, and the bill
 * worth chasing hardest is the oldest one, which a period scope would hide.
 *
 * A 404 MEANS THE `payments` MODULE IS OFF (§18.3). Read it with `isFeatureOff`
 * and render nothing: no alert, no retry — pressing a button cannot switch a
 * flag on — and above all no "you do not have permission", which is exactly what
 * the 404 exists to hide.
 */
export function useOutstandingAgeing(
  options: { enabled?: boolean } = {},
): UseQueryResult<OutstandingAgeingResponse> {
  return useQuery({
    queryKey: queryKeys.reports.ageing(),
    queryFn: () => api.get<OutstandingAgeingResponse>(endpoints.reportsOutstandingAgeing),
    enabled: options.enabled ?? true,
    staleTime: 30_000,
    /*
     * A disabled module is not a transient failure, and retrying a 404 three
     * times on a workshop connection just delays the screen settling.
     */
    retry: false,
  });
}

/**
 * The twelve columns of the billed-by-month chart, for the financial year the
 * chosen period falls in.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT IS `GET /dashboard/summary?fy=`, AND NOT A REPORTS ENDPOINT, BECAUSE THE
 * SERIES ALREADY EXISTS THERE. `monthlyBilled` is April→March with every month
 * present including the empty ones, and it is the very series the home chart
 * plots — so Reports and Home cannot draw two different shapes for one year.
 *
 * IT IS GROSS OF CREDIT NOTES, and the screen says so beside the chart. The
 * dashboard's monthly series sums invoices only; the headline figures above it
 * are NET (§17.3). Those are two different questions and the caption names
 * which, rather than the chart quietly disagreeing with the card above it.
 *
 * KEYED UNDER `reports`, NOT UNDER `dashboard` — see `lib/query-keys.ts`. Home
 * reads the same endpoint with no `?fy=`, and sharing a cache entry would let a
 * Reports screen showing last financial year overwrite what Home paints from.
 *
 * `fy` is the `2026-27` label off the summary response, so the year is the
 * SERVER's answer for the chosen period rather than one this file derived —
 * which matters in April, when "last month" is in the previous financial year.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function useReportMonthlyBilled(
  fyLabel: string | undefined,
): UseQueryResult<DashboardSummaryResponse> {
  return useQuery({
    queryKey: queryKeys.reports.monthly(fyLabel ?? ""),
    queryFn: () =>
      api.get<DashboardSummaryResponse>(
        `${endpoints.dashboardSummary}?fy=${encodeURIComponent(fyLabel ?? "")}`,
      ),
    enabled: fyLabel !== undefined && fyLabel !== "",
    staleTime: 30_000,
  });
}

/* -------------------------------------------------------------------------- */
/* The export (§17.4)                                                          */
/* -------------------------------------------------------------------------- */

/**
 * The files the CA is sent, in the order they are useful.
 *
 * THE REGISTER IS FIRST BECAUSE IT IS THE ONE THAT CLOSES THE LOOP — §17.4:
 * "this alone replaces the can-you-send-me-last-month's-bills loop". The four
 * GSTR-1 tables follow, and ALL FOUR are offered: the one a shorter list would
 * drop is CDNR, whose absence over-reports the owner's liability.
 */
export interface ReportExportFile {
  /** Stable id — the button key, the busy flag, and the i18n key suffix. */
  id: string;
  labelKey: string;
  helpKey: string;
  /** Built with the period the screen is showing. */
  path: (preset: ReportPeriodPreset) => string;
  /** Used only when the server sends no `Content-Disposition`. */
  filenameKind: string;
}

const GSTR1_FILES: readonly { section: Gstr1Section; id: string }[] = [
  { section: "b2b", id: "b2b" },
  { section: "cdnr", id: "cdnr" },
  { section: "hsn", id: "hsn" },
  { section: "docs", id: "docs" },
];

export const REPORT_EXPORT_FILES: readonly ReportExportFile[] = [
  {
    id: "register",
    labelKey: "reports.export.register",
    helpKey: "reports.export.registerHelp",
    path: (preset) => `${endpoints.reportsRegisterCsv}${reportPeriodQuery(preset)}`,
    filenameKind: "register",
  },
  ...GSTR1_FILES.map(({ section, id }) => ({
    id,
    labelKey: `reports.export.${id}`,
    helpKey: `reports.export.${id}Help`,
    path: (preset: ReportPeriodPreset) =>
      `${endpoints.reportsGstr1Csv}${reportPeriodQuery(preset)}&section=${section}`,
    filenameKind: `gstr1-${section}`,
  })),
];

/**
 * Fetch one CSV and save it to the device.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NOT AN `<a href>`, AND NOT `api.get`. The export routes are bearer-
 * authenticated exactly like the PDF routes (§1.1), and a browser navigation
 * sends no Authorization header — it would 401, and the 401 would look to
 * `lib/api.ts` like an expired session and sign him out mid-export. `api.get`
 * is equally wrong in the other direction: it reads the body as text and parses
 * it as JSON, so a CSV would come back as `undefined` with no error at all.
 *
 * `Accept: "text/csv"` OVERRIDES `requestBlob`'s PDF default because that
 * function spreads `options.headers` AFTER its own — a deliberate ordering, and
 * the reason a second blob helper was not needed.
 *
 * THE FILENAME COMES FROM THE SERVER when it sends one
 * (`steelbill-register-2026-08-01-to-2026-08-31.csv`), because the period is IN
 * it and that is what makes a folder of exports navigable. The fallback here is
 * only for a response with no `Content-Disposition`.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export async function downloadReportCsv(file: ReportExportFile, period: {
  preset: ReportPeriodPreset;
  from: string;
  to: string;
}): Promise<void> {
  const { blob, filename } = await requestBlob(file.path(period.preset), {
    fallbackFilename: `steelbill-${file.filenameKind}-${period.from}-to-${period.to}.csv`,
    headers: { Accept: "text/csv" },
  });

  downloadBlob(blob, filename);
}

/* -------------------------------------------------------------------------- */

/**
 * Re-exported so the ageing chart imports one module rather than two.
 *
 * IT IS THE PAYMENTS MODULE'S FUNCTION, NOT A COPY. "A 404 means the feature is
 * off" is one rule (§18.3), and a second implementation of it here would be a
 * second place for someone to decide that a 403 counts too — which is precisely
 * the leak the 404 exists to prevent.
 */
export { isFeatureOff } from "@/features/payments/use-payments";
